import bernasos from '../../shared/assets/images/logos/topShoppingNetwork/bernasos.svg';
import bosch from '../../shared/assets/images/logos/topShoppingNetwork/bosch.svg';
import cairoSales from '../../shared/assets/images/logos/topShoppingNetwork/cairoSales.svg';
import concrete from '../../shared/assets/images/logos/topShoppingNetwork/concrete.svg';
import dream from '../../shared/assets/images/logos/topShoppingNetwork/dream.svg';
import dubaiPhone from '../../shared/assets/images/logos/topShoppingNetwork/dubaiPhone.svg';
import elaraby from '../../shared/assets/images/logos/topShoppingNetwork/elaraby.svg';
import fathallaMarket from '../../shared/assets/images/logos/topShoppingNetwork/fathalla.svg';
import happyVision from '../../shared/assets/images/logos/topShoppingNetwork/happyVision.svg';
import iqStore from '../../shared/assets/images/logos/topShoppingNetwork/iqStore.svg';
import kabbani from '../../shared/assets/images/logos/topShoppingNetwork/kabbani.svg';
import mac from '../../shared/assets/images/logos/topShoppingNetwork/mac.svg';
import miamiCenter from '../../shared/assets/images/logos/topShoppingNetwork/miamiCenter.svg';
import mobilaty from '../../shared/assets/images/logos/topShoppingNetwork/mobilaty.svg';
import samsung from '../../shared/assets/images/logos/topShoppingNetwork/samsung.svg';
import sevenSecrets from '../../shared/assets/images/logos/topShoppingNetwork/seven.svg';
import switchPlus from '../../shared/assets/images/logos/topShoppingNetwork/switchPlus.svg';
import taybyPharmacies from '../../shared/assets/images/logos/topShoppingNetwork/tay.svg';
import townTeam from '../../shared/assets/images/logos/topShoppingNetwork/townTeam.svg';
import tradeline from '../../shared/assets/images/logos/topShoppingNetwork/tradeline.svg';
import tseppas from '../../shared/assets/images/logos/topShoppingNetwork/tseppas.svg';
import xiaomi from '../../shared/assets/images/logos/topShoppingNetwork/xiaomi.svg';
import xprs from '../../shared/assets/images/logos/topShoppingNetwork/xprs.svg';
import zadna from '../../shared/assets/images/logos/topShoppingNetwork/zadna.svg';
import { useLanguage } from '../context/LanguageContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { motion } from "framer-motion";
import { fadeIn, fadeUp, staggerContainer } from "../lib/motion";

const TopShoppingNetwork = () => {
  const { translation, isRTL } = useLanguage()
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "end",
    direction: isRTL ? "rtl" : "ltr",
    slidesToScroll: 1,
    breakpoints: {
      "(min-width: 768px)": { slidesToScroll: 1 },
    },
  })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const sectionRef = useRef<HTMLDivElement | null>(null)

  const scrollTo = useCallback(
      (index: number) => emblaApi && emblaApi.scrollTo(index),
      [emblaApi]
    )
  
    const onSelect = useCallback(() => {
      if (!emblaApi) return
      setSelectedIndex(emblaApi.selectedScrollSnap())
    }, [emblaApi])
  
    useEffect(() => {
      if (!emblaApi) return
      onSelect()
      setScrollSnaps(emblaApi.scrollSnapList())
      emblaApi.on("select", onSelect)
      emblaApi.on("reInit", onSelect)
  
      return () => {
        emblaApi.off("select", onSelect)
        emblaApi.off("reInit", onSelect)
      }
    }, [emblaApi, onSelect])

    useEffect(() => {
      if (!emblaApi) return
      if (!sectionRef.current) return

      let intervalId: ReturnType<typeof setInterval> | null = null

      const startAutoScroll = () => {
        if (intervalId) return
        intervalId = setInterval(() => {
          const nextIndex = emblaApi.selectedScrollSnap() + 1
          emblaApi.scrollTo(nextIndex % emblaApi.scrollSnapList().length)
        }, 3500)
      }

      const stopAutoScroll = () => {
        if (!intervalId) return
        clearInterval(intervalId)
        intervalId = null
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            startAutoScroll()
          } else {
            stopAutoScroll()
          }
        },
        { threshold: 0.35 }
      )

      observer.observe(sectionRef.current)

      return () => {
        observer.disconnect()
        stopAutoScroll()
      }
    }, [emblaApi])
  
    const slides = [
      [tradeline, xprs, elaraby, fathallaMarket, dream, dubaiPhone, kabbani, townTeam],
      [mac, happyVision, bosch, samsung, switchPlus, xiaomi, tseppas, taybyPharmacies],
      [sevenSecrets, bernasos, concrete, iqStore, miamiCenter, cairoSales, mobilaty, zadna],
    ];

  return (
    <div>
      <motion.main
        ref={sectionRef}
        className="flex flex-col justify-center items-center gap-9 lg:gap-12 w-full px-5 md:px-0 lg:px-[3.125rem]"
        variants={fadeIn}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
      >
        <motion.div className='flex flex-col justify-center items-center gap-1.5 md:gap-6 text-primary' variants={staggerContainer(0.1, 0.05)}>
          <motion.div className='text-2xl md:text-[2.5rem] leading-[100%] font-bold text-center' variants={fadeUp}>
            {translation.topShoppingNetwork}
          </motion.div>
          <div className={`text-base lg:text-[1.375rem] ${isRTL ? 'md:w-[55%]' : ''} text-center`}>{translation.buyNow}</div>
        </motion.div>
        <div className="overflow-hidden w-full pt-1" ref={emblaRef}>
          <div className="flex">
            {slides.map((logos, index) => (
              <motion.div key={index} className='flex-[0_0_100%] flex flex-col justify-center items-center gap-8 md:gap-[4.375rem] w-full px-[1.25rem] lg:px-0' variants={staggerContainer(0.08, 0.04)}>
                <motion.div className='grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 items-center justify-center gap-8' variants={staggerContainer(0.08, 0.04)}>
                  {logos.slice(0, 4).map((logo, i) => (
                    <motion.img
                      key={i}
                      src={logo}
                      alt="logo"
                      className="mx-auto hover-scale origin-center"
                      variants={fadeUp}
                      whileHover={{ scale: 1.06 }}
                      transition={{ type: "spring", stiffness: 260, damping: 18 }}
                    />
                  ))}
                </motion.div>
                <motion.div className='grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 items-center justify-center gap-8' variants={staggerContainer(0.08, 0.04)}>
                  {logos.slice(4, 8).map((logo, i) => (
                    <motion.img
                      key={i}
                      src={logo}
                      alt="logo"
                      className="mx-auto hover-scale origin-center"
                      variants={fadeUp}
                      whileHover={{ scale: 1.06 }}
                      transition={{ type: "spring", stiffness: 260, damping: 18 }}
                    />
                  ))}
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.main>
      <div className="flex justify-center items-center gap-2 mt-12">
        {scrollSnaps.map((_, index) => (
          <button
            key={index}
            className={`rounded-full transition-all ${index === selectedIndex ? "bg-softGreen w-2.5 h-2.5" : "bg-[#D8D8D8] w-1.5 h-1.5"
              }`}
            onClick={() => scrollTo(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  )
}

export default TopShoppingNetwork
